import { useCallback, useEffect, useRef, useState } from "react";
import { Trash2, Upload, Loader2, Film } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fileUrl } from "@/lib/api";
import { GALLERY_CATEGORIES } from "@/lib/data";

const CATS = GALLERY_CATEGORIES.filter((c) => c !== "All");
const inputCls = "w-full border border-white/15 bg-transparent px-3 py-2.5 text-sm text-bone placeholder:text-zinc-500 focus:border-brand focus:outline-none";

export const GalleryTab = () => {
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(CATS[0]);
  const [description, setDescription] = useState("");
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/media").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) return toast.error("Choose a file and add a title.");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("category", category);
    fd.append("description", description);
    setProgress(0);
    try {
      await api.post("/media", fd, { onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)) });
      toast.success("Uploaded to your gallery");
      setFile(null); setTitle(""); setDescription("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setProgress(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this from the gallery?")) return;
    try {
      await api.delete(`/media/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="gallery-tab" className="grid gap-8 lg:grid-cols-12">
      <form onSubmit={upload} className="space-y-4 border border-white/10 bg-coal p-6 lg:col-span-4">
        <h2 className="font-display text-lg font-extrabold uppercase">Add photo or video</h2>
        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-white/20 px-4 py-8 text-center text-sm text-zinc-300 hover:border-brand">
          <Upload className="h-5 w-5 text-brand" />
          <span data-testid="gallery-file-name">{file ? file.name : "Click to choose a photo (JPG, PNG, WEBP) or video (MP4, MOV, WEBM)"}</span>
          <input ref={fileRef} data-testid="gallery-file-input" type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <input data-testid="gallery-title-input" className={inputCls} placeholder="Title (e.g. BMW X5 smart key)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="gallery-category-select" className="rounded-none border-white/15 bg-transparent"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none border-white/10 bg-coal text-bone">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <textarea data-testid="gallery-description-input" rows={2} className={`${inputCls} resize-none`} placeholder="Short description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
        <button data-testid="gallery-upload-button" disabled={progress !== null} className="inline-flex w-full items-center justify-center gap-2 bg-brand py-3 font-display text-sm font-extrabold uppercase tracking-wider text-ink disabled:opacity-60">
          {progress !== null ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading {progress}%</> : "Upload"}
        </button>
        <p className="text-xs text-zinc-400">Until you upload your own work, the website shows sample photos.</p>
      </form>
      <div className="lg:col-span-8">
        <div className="mb-4 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">{items.length} items live on the website</div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {items.map((m) => (
            <div key={m.id} data-testid={`admin-media-${m.id}`} className="group relative aspect-square overflow-hidden border border-white/10 bg-coal">
              <img src={fileUrl(m.thumb_path || m.storage_path)} alt={m.title} loading="lazy" className="h-full w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink to-transparent p-3">
                <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.14em] text-brand">{m.media_type === "video" && <Film className="h-3 w-3" />}{m.category}</div>
                <div className="truncate text-sm font-semibold">{m.title}</div>
              </div>
              <button data-testid={`admin-media-delete-${m.id}`} onClick={() => remove(m.id)} className="absolute right-2 top-2 grid h-8 w-8 place-items-center bg-ink/80 text-zinc-300 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        {!items.length && <p data-testid="admin-media-empty" className="border border-dashed border-white/15 p-10 text-center text-sm text-zinc-400">No uploads yet.</p>}
      </div>
    </div>
  );
};
